// src/pages/vouchers/VouchersListPage/VouchersListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import VoucherCreateModal from "../VoucherCreateModal/VoucherCreateModal";
import VouchersAPI, { normalizeVoucher, exportVouchersURL } from "../../../api/v3/vouchers-api-v3";

const BASE = "/app/sales/bagspa";

const includesCi = (haystack = "", needle = "") =>
  (haystack ?? "").toString().toLowerCase().includes((needle || "").toLowerCase());

const isTrue = (v) => {
  if (v === true || v === 1) return true;
  const s = String(v).toLowerCase();
  return s === "1" || s === "true" || s === "t" || s === "yes" || s === "y";
};

export default function VouchersListPage() {
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  function onVoucherCreated(item) {
    const voucher = normalizeVoucher(item);
    setItems(previous => [voucher, ...previous.filter(row => row.id !== voucher.id)]);
    setQ(voucher.code);
    setScope("all");
    setOnlyActive(false);
    setPage(1);
    setCreateOpen(false);
  }
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [scope, setScope] = useState("all"); // all | all_items | product_categories | order_categories

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // --- pagination ---
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25); // 10/25/50/100

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const rows = await VouchersAPI.listNormalized(); // normalized, booleans fixed
        setItems(Array.isArray(rows) ? rows : []);
      } catch (e) {
        setErr(e.message || "Failed fetching vouchers");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Reset to first page whenever filters change
  useEffect(() => {
    setPage(1);
  }, [q, onlyActive, scope]);

  const filtered = useMemo(() => {
    let r = items;
    if (onlyActive) r = r.filter((x) => isTrue(x.active));
    if (scope !== "all") r = r.filter((x) => (x.applies_to_scope || "all_items") === scope);
    if (q) {
      r = r.filter(
        (x) =>
          includesCi(x.code, q) ||
          includesCi(x.promotional_text, q) ||
          includesCi(x.discount_type, q)
      );
    }
    return r;
  }, [items, q, onlyActive, scope]);

  // --- paginate (client-side like CustomerListPage) ---
  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * per;
    return filtered.slice(start, start + per);
  }, [filtered, pageSafe, per]);

  if (loading) return <div>Loading…</div>;
  if (err) return <div className="alert alert-danger">{err}</div>;

  return (
    <div className="bagspa-page vouchers-page">
      <div className="bagspa-page-header vouchers-page-header">
        <h2 className="mb-0">Vouchers</h2>
      </div>

      <div className="bagspa-filter-card vouchers-control-card">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
          }}
        >
          {/* TOP ROW */}
          <div className="vouchers-control-top-row">
            <div className="bagspa-filter-field vouchers-filter-search">
              <label className="form-label">Search</label>
              <div className="input-group vouchers-search-group">
                <input
                  className="form-control"
                  placeholder="Code, promo text, type..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <div className="input-group-append">
                  <button type="submit" className="btn btn-primary">
                    Search
                  </button>
                </div>
              </div>
            </div>

            <div className="bagspa-filter-field vouchers-filter-scope">
              <label className="form-label">Scope</label>
              <select
                className="form-control"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              >
                <option value="all">All scopes</option>
                <option value="all_items">All items</option>
                <option value="product_categories">Product categories only</option>
                <option value="order_categories">Order categories/treatments</option>
              </select>
            </div>

            <div className="bagspa-filter-field vouchers-filter-active">
              <label className="form-label">Status</label>
              <div className="bagspa-check-row vouchers-check-row">
                <div className="form-check">
                  <input
                    id="onlyActive"
                    type="checkbox"
                    className="form-check-input"
                    checked={onlyActive}
                    onChange={(e) => setOnlyActive(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="onlyActive">
                    Only Active
                  </label>
                </div>
              </div>
            </div>

            <div className="bagspa-filter-field vouchers-filter-rows">
              <label className="form-label">Rows</label>
              <select
                className="form-control"
                value={per}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10) || 25;
                  setPer(n);
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </select>
            </div>

            <div className="vouchers-filter-performance">
              <a
                className="btn btn-primary"
                href={VouchersAPI.exportPerformanceURL({})}
                target="_blank"
                rel="noopener noreferrer"
              >
                Export Performance
              </a>
            </div>
          </div>

          {/* BOTTOM ROW */}
          <div className="vouchers-control-bottom-row">
            <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              + Create Voucher
            </button>

            <Link
              className="btn btn-outline-primary bagspa-btn-outline"
              to={`${BASE}/vouchers/import`}
            >
              Import CSV
            </Link>

            <a
              className="btn btn-primary bagspa-btn-outline"
              href={exportVouchersURL({ q })}
              target="_blank"
              rel="noopener noreferrer"
            >
              Export CSV
            </a>
          </div>
        </form>
      </div>

      {createOpen && (
        <VoucherCreateModal isOpen onClose={() => setCreateOpen(false)} onCreated={onVoucherCreated} />
      )}

      <div className="bagspa-table-card">
        <div className="table-responsive">
          <table className="table table-hover align-middle vouchers-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Value</th>
                <th>Scope</th>
                <th>Active</th>
                <th>Expiration</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>

            <tbody>
              {paged.map((x) => (
                <tr key={x.id || x.code}>
                  <td className="fw-semibold">{x.code}</td>
                  <td>{x.discount_type}</td>
                  <td>
                    {x.discount_type === "percentage"
                      ? `${x.discount_value}%`
                      : `Rp ${Number(x.discount_value || 0).toLocaleString("id-ID")}`}
                  </td>
                  <td>{x.applies_to_scope || "all_items"}</td>
                  <td>{isTrue(x.active) ? "Yes" : "No"}</td>
                  <td>
                    {x.expiration_date
                      ? new Date(x.expiration_date).toLocaleString("id-ID")
                      : "-"}
                  </td>
                  <td className="text-end">
                    <div className="btn-group">
                      <Link
                        className="btn btn-sm btn-outline-primary bagspa-btn-outline"
                        to={`${BASE}/vouchers/${x.id}`}
                      >
                        Detail
                      </Link>
                      <Link
                        className="btn btn-sm btn-primary"
                        to={`${BASE}/vouchers/${x.id}/edit`}
                      >
                        Edit
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}

              {paged.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No vouchers match your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bagspa-pagination-row vouchers-pagination-row mt-3">
          <div className="text-muted small">
            Total: {filtered.length} • Page {pageSafe} / {totalPages}
          </div>

          <div className="btn-group">
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe <= 1}
              onClick={() => setPage(1)}
            >
              &laquo;
            </button>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              &lsaquo;
            </button>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              &rsaquo;
            </button>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}